/**
 * Offline license verification.
 *
 * Token format:  DSP1.<base64url(payload JSON)>.<base64url(Ed25519 signature)>
 * The signature covers the raw payload bytes. Only the PUBLIC key is embedded here;
 * tokens are issued by the activation service (worker/) which holds the private key.
 * Verification is pure math — no network request is ever made by the app.
 */
import * as ed from "@noble/ed25519";

/** Replace with the output of `npm run keys:gen` (hex, 64 chars). */
export const LICENSE_PUBLIC_KEY_HEX = "06b24207fb2594a81f0c440828311f39d6610e8ab49fb5280b224be48adf661c";

export const FREE_FILE_LIMIT = 20;

export interface LicensePayload {
  v: 1;
  plan: "pro";
  sub: string; // masked email or license id
  iat: number; // unix seconds
  exp: number; // unix seconds, 0 = perpetual
  iss: "dicomscrub";
}

export interface LicenseState {
  valid: boolean;
  payload?: LicensePayload;
  reason?: string;
}

const STORAGE_KEY = "dicomscrub.license";

export function b64urlDecode(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function b64urlEncode(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export async function verifyToken(token: string, publicKeyHex = LICENSE_PUBLIC_KEY_HEX, now = Date.now()): Promise<LicenseState> {
  const t = token.trim();
  const parts = t.split(".");
  if (parts.length !== 3 || parts[0] !== "DSP1") return { valid: false, reason: "Malformed token" };
  let payload: LicensePayload;
  let payloadBytes: Uint8Array;
  let sig: Uint8Array;
  try {
    payloadBytes = b64urlDecode(parts[1]);
    sig = b64urlDecode(parts[2]);
    payload = JSON.parse(new TextDecoder().decode(payloadBytes)) as LicensePayload;
  } catch {
    return { valid: false, reason: "Malformed token" };
  }
  if (payload.iss !== "dicomscrub" || payload.v !== 1 || payload.plan !== "pro") {
    return { valid: false, reason: "Unknown issuer or plan" };
  }
  if (/^0+$/.test(publicKeyHex)) return { valid: false, reason: "App built without a license public key" };
  let ok = false;
  try {
    ok = await ed.verifyAsync(sig, payloadBytes, hexToBytes(publicKeyHex));
  } catch {
    ok = false;
  }
  if (!ok) return { valid: false, reason: "Invalid signature" };
  if (payload.exp && payload.exp * 1000 < now) return { valid: false, payload, reason: "License expired" };
  return { valid: true, payload };
}

export function loadStoredToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode: ignore */
  }
}
